import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import {PaletteColorsEnum} from '../../../libs/shared/ui/src/styles/paletteModel';
import {ChannelIcon, ChannelIconSizesEnum} from '../../components/common/channelIcon';
import {useLinkNavigation} from '../../components/common/navigation/useLinkNavigation';
import {FolderBreadcrumb} from '../../components/folderBreadcrumbs/folderBreadcrumbs';
import {findUniqueChannelType} from '../../helpers/models/channelHelpers';
import {useAppSelector} from '../../helpers/reduxHelpers';
import {ChannelModel} from '../../models/api/channelModel';
import {InboxModel} from '../../models/api/inboxModel';
import {buildSettingsPath} from '../../models/url/settingsUrlHelpers';
import {
  SettingsInboxesSubPagesEnum,
  SettingsInboxesUrl,
  SettingsResourcePagesEnum,
} from '../../models/url/settingsUrlModel';
import {getExternalChannelTypes} from '../../store/data/externalChannelTypes/externalChannelTypesSelectors';
import {SettingsBreadcrumbs} from '../common/settingsBreadcrumbs';
import {StyledSettingsFormHeaderButtons} from '../common/settingsWrapper';
import {SettingsHeader} from '../settingsHeader';

/*
 * Props.
 */

interface SettingsInboxesChannelHeaderProps {
  url: SettingsInboxesUrl;
  inbox: InboxModel;
  channel: ChannelModel;
}

/*
 * Style.
 */

const StyledChannelIcon = styled(ChannelIcon)`
  width: 24px;
  height: 24px;
`;

/*
 * Component.
 */

export const SettingsInboxesChannelHeader: FC<SettingsInboxesChannelHeaderProps> = (props) => {
  const {channel, inbox, url} = props;
  const externalChannelTypes = useAppSelector(getExternalChannelTypes);
  const uniqueType = findUniqueChannelType(channel, externalChannelTypes);
  const {navigateToLink} = useLinkNavigation();

  const renderTopLine = () => {
    const breadcrumbs: ReadonlyArray<FolderBreadcrumb> = [
      {
        label: inbox.name,
        onBreadcrumbClick: () => {
          navigateToLink(
            buildSettingsPath({
              ...url,
              subPage: SettingsInboxesSubPagesEnum.OVERVIEW,
              channelId: undefined,
              inboxId: inbox.id,
            }),
          );
        },
      },
    ];

    return (
      <SettingsBreadcrumbs
        showSeparatorAfterTopLevel
        topLevelBreadcrumb={{
          label: <Trans id="settings-inboxes">Inboxes</Trans>,
          onBreadcrumbClick: () => {
            navigateToLink(
              buildSettingsPath({
                ...url,
                subPage: undefined,
                resourcePage: SettingsResourcePagesEnum.OVERVIEW,
                channelId: undefined,
                inboxId: inbox.id,
              }),
            );
          },
        }}
        breadcrumbs={breadcrumbs}
      />
    );
  };

  const renderRight = () => (
    <StyledSettingsFormHeaderButtons>
      <Button size={VisualSizesEnum.MEDIUM} variant={ButtonVariantsEnum.SECONDARY} onClick={() => {}}>
        <Trans id="settings-movechannel">Move channel</Trans>
      </Button>
      <Button
        size={VisualSizesEnum.MEDIUM}
        variant={ButtonVariantsEnum.SECONDARY_DANGER}
        color={PaletteColorsEnum.RED}
        onClick={() => {}}
      >
        <Trans id="settings-deletechannel">Delete channel</Trans>
      </Button>
    </StyledSettingsFormHeaderButtons>
  );

  return (
    <SettingsHeader right={renderRight()} topLine={renderTopLine()}>
      <StyledChannelIcon size={ChannelIconSizesEnum.BIG} type={uniqueType} />
      {channel.displayName}
    </SettingsHeader>
  );
};
